/**
 * アプリケーションルート
 *
 * React Routerを使用したルーティング設定
 * LaravelでいうRouteServiceProviderに相当
 */

import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

// レイアウト
import Layout from "./components/Layout";
import OfflineIndicator from "./components/OfflineIndicator";

// 認証
import AuthGuard, {
  FacilityAdminGuard,
  FacilityMemberGuard,
  SystemAdminGuard,
} from "./components/AuthGuard";

// 同期サービス
import { initSyncService } from "./services/syncService";

// ページコンポーネント
import CalibrationPage from "./pages/CalibrationPage";
import ExamPreparePage from "./pages/ExamPreparePage";
import ExamResultPage from "./pages/ExamResultPage";
import ExamRunPage from "./pages/ExamRunPage";
import ExamSettingsPage from "./pages/ExamSettingsPage";
import FacilitiesPage from "./pages/FacilitiesPage";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import PatientDetailPage from "./pages/PatientDetailPage";
import PatientsPage from "./pages/PatientsPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";

// 仮のページコンポーネント（未実装ページ用）
const PlaceholderPage = ({ title }: { title: string }) => (
  <div>
    <h1 className="text-2xl font-bold text-gray-800 mb-4">{title}</h1>
    <p className="text-gray-600">このページは実装中です</p>
  </div>
);

/**
 * レイアウト付きルートのラッパー
 * 認証が必要なページはこのコンポーネントでラップされる
 */
const LayoutRoute = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard>
    <Layout>{children}</Layout>
  </AuthGuard>
);

/**
 * システム管理者専用のレイアウト付きルート
 */
const SystemAdminRoute = ({ children }: { children: React.ReactNode }) => (
  <SystemAdminGuard>
    <Layout>{children}</Layout>
  </SystemAdminGuard>
);

/**
 * 施設管理者以上専用のレイアウト付きルート
 */
const FacilityAdminRoute = ({ children }: { children: React.ReactNode }) => (
  <FacilityAdminGuard>
    <Layout>{children}</Layout>
  </FacilityAdminGuard>
);

/**
 * 施設所属ユーザー専用のレイアウト付きルート（SYSTEM_ADMINを除外）
 */
const FacilityMemberRoute = ({ children }: { children: React.ReactNode }) => (
  <FacilityMemberGuard>
    <Layout>{children}</Layout>
  </FacilityMemberGuard>
);

function App() {
  // 同期サービスの初期化（起動時同期 + オンライン復帰時の自動同期）
  useEffect(() => {
    const cleanup = initSyncService();
    return cleanup;
  }, []);

  return (
    <>
      <OfflineIndicator />
      <Routes>
        {/* ========================================
            認証不要ページ（レイアウトなし）
            ======================================== */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/calibration" element={<CalibrationPage />} />

        {/* ========================================
            認証必要ページ（レイアウトあり）
            LaravelでいうRoute::middleware('auth')->group()に相当
            ======================================== */}

        {/* ホーム */}
        <Route
          path="/home"
          element={
            <LayoutRoute>
              <HomePage />
            </LayoutRoute>
          }
        />

        {/* 施設管理（システム管理者のみ） */}
        <Route
          path="/facilities"
          element={
            <SystemAdminRoute>
              <FacilitiesPage />
            </SystemAdminRoute>
          }
        />

        {/* 患者管理（施設所属ユーザーのみ） */}
        <Route
          path="/patients"
          element={
            <FacilityMemberRoute>
              <PatientsPage />
            </FacilityMemberRoute>
          }
        />
        <Route
          path="/patients/new"
          element={
            <FacilityMemberRoute>
              <PlaceholderPage title="患者登録" />
            </FacilityMemberRoute>
          }
        />
        <Route
          path="/patients/:id"
          element={
            <FacilityMemberRoute>
              <PatientDetailPage />
            </FacilityMemberRoute>
          }
        />

        {/* 検査（施設所属ユーザーのみ） */}
        <Route
          path="/exam/settings"
          element={
            <FacilityMemberRoute>
              <ExamSettingsPage />
            </FacilityMemberRoute>
          }
        />
        <Route
          path="/exam/prepare"
          element={
            <FacilityMemberGuard>
              <ExamPreparePage />
            </FacilityMemberGuard>
          }
        />
        <Route
          path="/exam/run"
          element={
            <FacilityMemberGuard>
              <ExamRunPage />
            </FacilityMemberGuard>
          }
        />
        <Route
          path="/exam/result"
          element={
            <FacilityMemberRoute>
              <ExamResultPage />
            </FacilityMemberRoute>
          }
        />

        {/* 設定（施設所属ユーザーのみ） */}
        <Route
          path="/settings"
          element={
            <FacilityMemberRoute>
              <SettingsPage />
            </FacilityMemberRoute>
          }
        />
        <Route
          path="/sync"
          element={
            <LayoutRoute>
              <PlaceholderPage title="同期状態" />
            </LayoutRoute>
          }
        />
        <Route
          path="/users"
          element={
            <FacilityAdminRoute>
              <UsersPage />
            </FacilityAdminRoute>
          }
        />

        {/* ========================================
            404リダイレクト
            ======================================== */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </>
  );
}

export default App;
